import { enableAllPlugins, produce } from 'immer';

import {
  accountPermissions,
  computeAccessSelection,
  defaultPermissionStateValues,
  isAccountPermissionInferable,
  isAnyAssignablePermissionSelected,
  isAnyPermissionActivelySelected,
  isPermissionActivelySelected,
  isPermissionSelected,
  permissions,
} from 'components/helpers/users';

enableAllPlugins();

const accessControlPermissionsToggleReducer = (state, action) =>
  produce(state, (draftAccessControls) => {
    const { assignablePermissionsPerDivision, permission } = action.payload;

    const toggleOptions = {
      assignablePermissionsPerDivision,
      inheritedAccessSelection: buildInheritedAccessSelection(state),
      permission,
    };

    switch (action.type) {
      case 'TOGGLE_PERMISSION': {
        const { divisionId } = action.payload;

        const isSelected = !isPermissionSelected(
          draftAccessControls[divisionId]?.[permission],
        );

        assignPermissionOnToggledDivision(draftAccessControls, {
          ...toggleOptions,
          isSelected,
          toggledDivisionId: divisionId,
        });

        if (accountPermissions.includes(permission)) {
          propagateAccountPermission(draftAccessControls, {
            ...toggleOptions,
            isSelected,
            toggledDivisionId: divisionId,
          });
        }
        break;
      }
      case 'TOGGLE_SELECT_ALL': {
        const { divisionIds, isSelected } = action.payload;

        divisionIds.forEach((divisionId) => {
          if (
            isPermissionSelected(
              draftAccessControls[divisionId]?.[permission],
            ) === isSelected
          )
            return;

          assignPermissionOnToggledDivision(draftAccessControls, {
            ...toggleOptions,
            isSelected,
            toggledDivisionId: divisionId,
          });
        });
        break;
      }
    }
  });

function buildInheritedAccessSelection(accessControls) {
  const existingAccessSelection = Object.values(accessControls).find(Boolean);

  return {
    ...defaultPermissionStateValues,
    ...Object.fromEntries(
      accountPermissions
        .filter((permission) =>
          isPermissionSelected(existingAccessSelection?.[permission]),
        )
        .map((permission) => [permission, 'inferredSelected']),
    ),
  };
}

function assignPermissionOnToggledDivision(
  draftAccessControls,
  {
    assignablePermissionsPerDivision,
    inheritedAccessSelection,
    isSelected,
    permission,
    toggledDivisionId,
  },
) {
  const currentAccessSelection = draftAccessControls[toggledDivisionId];

  if (!currentAccessSelection && !isSelected) return;

  draftAccessControls[toggledDivisionId] = computeAccessSelection({
    currentAccessSelection: currentAccessSelection || inheritedAccessSelection,
    isSelected,
    permission,
  });

  applyEmptyAccessControlPruningCheck(draftAccessControls, {
    assignablePermissions: assignablePermissionsPerDivision[toggledDivisionId],
    divisionId: toggledDivisionId,
  });
}

function propagateAccountPermission(
  draftAccessControls,
  {
    assignablePermissionsPerDivision,
    isSelected,
    permission,
    toggledDivisionId,
  },
) {
  if (
    !isSelected &&
    !isAnyAssignablePermissionSelected({
      accessSelection: draftAccessControls[toggledDivisionId],
      assignablePermissions:
        assignablePermissionsPerDivision[toggledDivisionId],
      permissionsToCheck: permissions,
    })
  )
    return;

  Object.keys(draftAccessControls).forEach((accessControlDivisionId) => {
    const accessSelection = draftAccessControls[accessControlDivisionId];

    if (accessControlDivisionId === toggledDivisionId || !accessSelection)
      return;

    const previousSelection = accessSelection[permission];

    accessSelection[permission] =
      !isSelected ? 'unselected'
      : isPermissionActivelySelected(accessSelection[permission]) ?
        'activelySelected'
      : (
        isAccountPermissionInferable({
          accessSelection,
          assignablePermissions:
            assignablePermissionsPerDivision[accessControlDivisionId],
          permission,
        })
      ) ?
        'inferredSelected'
      : 'unselected';

    if (
      isPermissionSelected(previousSelection) &&
      !isPermissionSelected(accessSelection[permission])
    ) {
      applyEmptyAccessControlPruningCheck(draftAccessControls, {
        assignablePermissions:
          assignablePermissionsPerDivision[accessControlDivisionId],
        divisionId: accessControlDivisionId,
      });
    }
  });
}

function applyEmptyAccessControlPruningCheck(
  draftAccessControls,
  { assignablePermissions, divisionId },
) {
  const accessSelection = draftAccessControls[divisionId];

  if (
    !isAnyAssignablePermissionSelected({
      accessSelection,
      assignablePermissions,
      permissionsToCheck: permissions,
    }) &&
    !isAnyPermissionActivelySelected({
      accessSelection,
      permissionsToCheck: accountPermissions,
    })
  )
    draftAccessControls[divisionId] = null;
}

export { accessControlPermissionsToggleReducer };
